import classNames from 'classnames'
import React from 'react'
import { PortableTextBodyElement } from '../../utilities/blockContent'
import { isNullOrWhiteSpace } from '../../utilities/string'
import { MarketSection } from '../MarketSection'
import { RichTextArea } from '../PortableText/RichTextArea'
import { BasicBlockSerializer } from '../PortableText/serializers/blocks/BasicBlockSerializer'
import { SupText } from '../SupText'

export type InvestmentRangeGroup = {
  investments?: string[]
  title?: string
}

export type InvestmentRangeBusinessSegmentColor = 'primary' | 'secondary'

export type InvestmentRangeBusinessSegment = {
  color?: InvestmentRangeBusinessSegmentColor
  investmentRanges?: InvestmentRangeGroup[]
  title?: string
}

export interface InvestmentRangeProps {
  body?: PortableTextBodyElement[]
  businessSegments?: InvestmentRangeBusinessSegment[]
  footnotes?: string[]
  id?: string
  title?: string
}

export const InvestmentRange = ({
  body,
  businessSegments,
  footnotes,
  id,
  title,
}: InvestmentRangeProps) => (
  <MarketSection id={id}>
    <div
      className={classNames(
        'col-span-full flex flex-col p-30 gap-30 rounded-15 bg-shade-lightest-warm',
        'sm:mx-120',
        'md:mx-0',
        'lg:col-start-2 lg:col-span-6 lg:-mx-75 lg:p-60 lg:gap-60',
        'xl:col-start-2 xl:col-span-8 xl:-mx-45',
        'xxl:col-start-4 xxl:col-span-8'
      )}
    >
      {(!isNullOrWhiteSpace(title) || !!body?.length) && (
        <header className="flex flex-col gap-30 lg:max-w-[80%]">
          {!isNullOrWhiteSpace(title) && (
            <h2 className="font-h2 text-h2 text-secondary-darker">{title}</h2>
          )}

          {!!body?.length && (
            <div className="font-body text-body text-secondary-darker">
              <RichTextArea
                components={{
                  block: BasicBlockSerializer,
                }}
                value={body}
              />
            </div>
          )}
        </header>
      )}

      {(!!businessSegments?.length || !!footnotes?.length) && (
        <div>
          {!!businessSegments?.length && (
            <div className="flex flex-col gap-20 lg:gap-30">
              <div className="flex gap-30 self-center">
                {businessSegments.map((businessSegment, index) => (
                  <div className="flex-none flex gap-10 items-center" key={index}>
                    <div
                      className={classNames('flex-none w-[16px] h-[16px] rounded-5', {
                        'bg-primary': businessSegment.color === 'primary',
                        'bg-shade': businessSegment.color === 'secondary',
                      })}
                    ></div>

                    <SupText className="font-t-bold text-t-400 text-secondary-darkest">
                      {businessSegment.title}
                    </SupText>
                  </div>
                ))}
              </div>

              <div className="flex flex-col gap-15 lg:flex-row lg:gap-30">
                {businessSegments.map((businessSegment, index) => (
                  <div
                    className={classNames(
                      'flex flex-col gap-20 p-15 rounded-15 tb:flex-row lg:p-30 lg:gap-30',
                      {
                        'bg-primary': businessSegment.color === 'primary',
                        'bg-shade': businessSegment.color === 'secondary',
                        'lg:basis-[33%]': businessSegment.investmentRanges?.length === 1,
                        'lg:basis-[67%]': businessSegment.investmentRanges?.length === 2,
                      }
                    )}
                    key={index}
                  >
                    {businessSegment.investmentRanges?.map(
                      (investmentRange, investmentRangeIndex) => (
                        <div
                          className="flex flex-col gap-10 w-full"
                          key={investmentRangeIndex}
                        >
                          {!isNullOrWhiteSpace(investmentRange.title) && (
                            <SupText
                              as="h3"
                              className="font-t-bold text-t-500 text-secondary-darkest text-center"
                            >
                              {investmentRange.title}
                            </SupText>
                          )}

                          {!!investmentRange.investments?.length && (
                            <div className="flex flex-col p-15 gap-10 bg-neutral-lighter-alt rounded-15">
                              {investmentRange.investments?.map(
                                (investment, investmentIndex) => (
                                  <SupText
                                    className="px-15 py-10 font-t text-t-400 text-secondary-darkest bg-neutral rounded-10"
                                    key={investmentIndex}
                                  >
                                    {investment}
                                  </SupText>
                                )
                              )}
                            </div>
                          )}
                        </div>
                      )
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {!!footnotes?.length && (
            <div className="mt-15 font-t-light text-t-400 text-secondary-darker lg:mt-30">
              {footnotes.map((footnote, index) => (
                <div key={index}>{footnote}</div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  </MarketSection>
)
